import React from 'react';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { useCurrentUser } from 'src/modules/shared/hooks/api/admin/users';
import { PropertyPermission } from 'src/modules/shared/types/permissions.type';
import {
  PropertyMediaKind,
  PropertyOperation,
  type CatalogProperty,
  type PropertyPortalPublication,
} from 'src/api/generated';
import { PiInfoLight } from 'react-icons/pi';
import Label from 'src/modules/shared/components/atoms/inputs/label';
import Select from 'src/modules/shared/components/atoms/inputs/select';
import { StatusPill } from 'src/modules/shared/components/atoms/status-pill';
import { Text } from 'src/modules/shared/components/atoms/text';
import { TextSkeleton } from 'src/modules/shared/components/atoms/text/text-skeleton';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { MediaThumbnailSelector } from 'src/modules/shared/components/molecules/media-thumbnail-selector';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { PropertyMeliDraftValidationCard } from '../molecules/property-meli-draft-validation-card';
import { PropertyMeliPublicationStatusSection } from '../molecules/property-meli-publication-status-section';
import { PropertySyncManagedNotice } from '../molecules/property-sync-managed-notice';
import { PropertyMeliPublishingManagerActionBar } from './property-meli-publishing-manager-action-bar';
import { getPropertySourceProviderLabelKey } from '../../utils/property-source-integration.util';
import {
  type MeliPublicationAction,
  type SupportedMeliOperation,
} from '../../utils/meli-publication.util';
import { usePropertyMeliPublishingManager } from '../../hooks/use-property-meli-publishing-manager';

interface QueuedPublicationAction {
  action: MeliPublicationAction;
  operation: SupportedMeliOperation;
}

interface PropertyMeliPublishingManagerProps {
  property: CatalogProperty;
  selectedOperation: SupportedMeliOperation;
  selectedPublicationId?: number | null;
  publications: PropertyPortalPublication[];
  refetchPublications: () => Promise<unknown>;
  setQueuedAction: (value: QueuedPublicationAction | null) => void;
  isSyncMode?: boolean;
  showHeader?: boolean;
}

/**
 * Inline MeLi publication manager embedded in the property editor.
 */
export const PropertyMeliPublishingManager: React.FC<
  PropertyMeliPublishingManagerProps
> = ({
  property,
  selectedOperation,
  selectedPublicationId = null,
  publications,
  refetchPublications,
  setQueuedAction,
  isSyncMode = false,
  showHeader = true,
}) => {
  const { t } = useI18n();
  const { hasPermission: canPublishAll } = useHasPermissions(
    PropertyPermission.Publish
  );
  const { hasPermission: canPublishScoped } = useHasPermissions(
    PropertyPermission.PublishScoped
  );
  const { user } = useCurrentUser();
  const canPublish =
    canPublishAll ||
    (canPublishScoped && user != null && property.userId === user.id);
  const sourceProviderLabel = t(getPropertySourceProviderLabelKey('meli'));
  const { actions, draft, form, loading, publication, sync } =
    usePropertyMeliPublishingManager({
      property,
      selectedOperation,
      selectedPublicationId,
      publications,
      refetchPublications,
      setQueuedAction,
      isSyncMode,
    });
  const currentPublicationId = publication.currentPublication?.id ?? null;

  return (
    <div className="space-y-5">
      {publication.isSyncManagedProperty ? (
        <PropertySyncManagedNotice providerLabel={sourceProviderLabel} t={t} />
      ) : null}

      {showHeader ? (
        <>
          <div className="flex flex-col gap-2">
            <Text size="small" weight="semibold" className="text-slate-900">
              {t('catalog.properties.meli.modal.title')}
            </Text>
            <Text size="tiny" className="text-slate-500">
              {t('catalog.properties.meli.modal.description')}
            </Text>
          </div>

          <div className="mt-4 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-2">
            <Text size="tiny" className="text-slate-500">
              {t('catalog.properties.meli.form.selectedOperation')}
            </Text>
            <StatusPill
              label={t(
                selectedOperation === PropertyOperation.SALE
                  ? 'catalog.properties.operation.SALE'
                  : 'catalog.properties.operation.RENT'
              )}
              variant="secondary"
            />
          </div>
        </>
      ) : null}

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <Label size="md">
            {t('catalog.properties.meli.form.portalTarget')}
          </Label>
          <Select
            options={form.portalTargetOptions}
            value={form.portalTarget}
            onChange={(event) =>
              actions.handlePortalTargetChange(event.target.value)
            }
          />
        </div>

        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Label size="md">
              {t('catalog.properties.meli.category.resolvedLabel')}
            </Label>
            <Tooltip
              content={t('catalog.properties.meli.category.autoResolved')}
              size="xs"
            >
              <span className="inline-flex">
                <PiInfoLight className="h-4 w-4 text-slate-400" />
              </span>
            </Tooltip>
          </div>
          {draft.data?.resolvedCategory ? (
            <div className="space-y-1 py-1">
              <Text size="small" weight="medium" className="text-slate-900">
                {draft.data.resolvedCategory.categoryName}
              </Text>
              <Text size="tiny" className="text-slate-500">
                {draft.data.resolvedCategory.domainName} ·{' '}
                {draft.data.resolvedCategory.categoryId}
              </Text>
            </div>
          ) : (
            <Text size="small" className="text-slate-500">
              {t('catalog.properties.meli.category.unavailable')}
            </Text>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <Label size="md">
            {t('catalog.properties.meli.form.listingType')}
          </Label>
          {draft.loading && !draft.data ? (
            <TextSkeleton className="h-9" />
          ) : (
            <Select
              options={form.listingTypeOptions}
              value={form.effectiveListingTypeId}
              onChange={(event) => {
                actions.setListingTypeId(event.target.value);
                actions.setDraft((currentDraft) =>
                  currentDraft
                    ? {
                        ...currentDraft,
                        listingTypeId: event.target.value || null,
                      }
                    : currentDraft
                );
              }}
            />
          )}
        </div>
      </div>

      <div>
        <MediaThumbnailSelector
          items={property.media.filter(
            (item) => item.kind === PropertyMediaKind.IMAGE
          )}
          selectedFileIds={form.selectedPictureFileIds}
          title={t('catalog.properties.meli.images.title')}
          description={t('catalog.properties.meli.images.description')}
          emptyLabel={t('catalog.properties.meli.images.empty')}
          selectable={false}
        />
      </div>

      <PropertyMeliPublicationStatusSection
        alerts={publication.alerts}
        currentPublication={publication.currentPublication}
        publicationEvents={publication.publicationEvents}
        t={t}
      />

      <PropertyMeliDraftValidationCard
        draft={draft.data}
        draftIssueVariant={draft.issueVariant}
        getDraftIssueFieldLabel={actions.getDraftIssueFieldLabel}
        getDraftIssueMessage={actions.getDraftIssueMessage}
        groupedDraftIssues={draft.groupedIssues}
        preparingDraft={draft.loading}
        selectedListingTypeName={
          form.selectedListingTypeOption?.content ??
          t('catalog.properties.meli.form.listingType.default')
        }
        t={t}
      />

      {canPublish ? (
        <PropertyMeliPublishingManagerActionBar
          currentPublication={publication.currentPublication}
          currentPublicationId={currentPublicationId}
          draftPublishBlockedByIssues={draft.publishBlockedByIssues}
          enqueueingPause={loading.enqueueingPause}
          enqueueingPublish={loading.enqueueingPublish}
          enqueueingUnpublish={loading.enqueueingUnpublish}
          handleEnqueuePublish={actions.handleEnqueuePublish}
          handlePersistedAction={actions.handlePersistedAction}
          isPropertyActive={property.isActive === true}
          isSyncMode={sync.isSyncMode}
          loadingAction={loading.action}
        />
      ) : null}
    </div>
  );
};
